import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PlayCircle, MessageSquareText, Lightbulb, Target, RotateCcw, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

const ACCENT = "#4FA3D9";
const TM = () => <sup className="align-super text-[0.5em] text-white/45">™</sup>;

const STEPS = [
  { icon: <PlayCircle size={22} strokeWidth={1.3} />, title: "Experience", text: "You step into a realistic scenario and experience the situation as it develops." },
  { icon: <MessageSquareText size={22} strokeWidth={1.3} />, title: "Respond", text: "Choose how you would respond as the situation unfolds." },
  { icon: <Lightbulb size={22} strokeWidth={1.3} />, title: "Reflect", text: "Look back at the choices you made and how you navigated the situation." },
  { icon: <Target size={22} strokeWidth={1.3} />, title: "Understand", text: "See what your responses reveal and where further practice could help." },
  { icon: <RotateCcw size={22} strokeWidth={1.3} />, title: "Practise Again", text: "Return to the situation and explore a different approach." },
];

export default function HowItWorks() {
  return (
    <div className="relative h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="how-it-works-page">
      <Seo
        title="How Adaptience Works | Interactive Life Simulation Practice"
        description="See how Adaptience works — enter a realistic situation, respond in your own words, reflect on how you handled it, then practise again before facing it for real."
        canonical="/how-it-works"
      />
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[60vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[45vh] w-[70vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.09)" }} />
      </div>
      <Navbar />
      <main className="relative z-10 mx-auto w-full max-w-4xl px-6 py-16 sm:px-10 sm:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: ACCENT }}>How It Works</p>
          <h1 className="max-w-2xl font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            Step into the situation.
            <span className="block" style={{ color: ACCENT }}>See what you would do.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/60">
            Experience a realistic situation, make your own decisions, and see how your response unfolds.
          </p>

          <div className="mt-16 flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-3" data-testid="how-steps">
            {STEPS.map((s, i, arr) => (
              <div key={s.title} className="flex flex-1 flex-col gap-6 sm:flex-row sm:items-start sm:gap-3">
                <div className="flex-1" data-testid={`step-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/12" style={{ color: ACCENT }}>{s.icon}</div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/90">{s.title}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/50">{s.text}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="flex items-center justify-center pt-3 text-white/25 sm:pt-5">
                    <ArrowRight size={18} className="hidden sm:block" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-20 border-l-2 pl-6" style={{ borderColor: ACCENT }}>
            <p className="font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl">Advice tells us what we should do.</p>
            <p className="mt-1 font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl" style={{ color: ACCENT }}>Practice reveals what we actually would do.</p>
          </div>

          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/65">
            Complete a simulation and receive your personal Adaptience<TM /> Practice Report — helping you understand your experience and what you could practise next.
          </p>

          <Link
            to="/simulations"
            data-testid="how-explore-btn"
            className="group mt-16 inline-flex items-center gap-3 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]"
            style={{ background: ACCENT }}
          >
            Explore Simulations
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <div className="mt-24">
          <SiteFooter />
        </div>
      </main>
    </div>
  );
}
